import themeConfig from './theme.config.json';

export const THEME_STORAGE_KEY = 'mova-explorer-theme-preference';
export const THEME_SESSION_KEY = 'mova-explorer-theme-session';
export const THEME_MODES = ['light', 'dark', 'system'];

export const cssVars = {
  light: themeConfig.light,
  dark: themeConfig.dark,
};

export const CSS_VAR_MAP = themeConfig.cssVarMap;

export function getSystemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getSessionThemeOverride() {
  const session = window.sessionStorage.getItem(THEME_SESSION_KEY);
  if (session === 'light' || session === 'dark') {
    return session;
  }
  return null;
}

export function isFollowingSystem(preference = getThemePreference()) {
  return preference === 'system';
}

export function getThemePreference() {
  const sessionOverride = getSessionThemeOverride();
  if (sessionOverride) {
    return sessionOverride;
  }
  return 'system';
}

export function persistThemePreference(preference) {
  if (preference === 'light' || preference === 'dark') {
    window.sessionStorage.setItem(THEME_SESSION_KEY, preference);
    return;
  }
  window.sessionStorage.removeItem(THEME_SESSION_KEY);
}

export function resetToSystemPreference() {
  window.sessionStorage.removeItem(THEME_SESSION_KEY);
  window.localStorage.setItem(THEME_STORAGE_KEY, 'system');
  return 'system';
}

export function resolveTheme(preference = getThemePreference()) {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
}

export function applyThemeCssVars(theme) {
  const colors = cssVars[theme] || cssVars.light;
  const root = document.documentElement;

  Object.entries(CSS_VAR_MAP).forEach(([jsKey, cssKey]) => {
    root.style.setProperty(`--${cssKey}`, colors[jsKey]);
  });
}

export function applyResolvedTheme(preference = getThemePreference()) {
  const theme = resolveTheme(preference);
  document.documentElement.setAttribute('data-theme', theme);
  applyThemeCssVars(theme);
  return theme;
}

export function getInitialTheme() {
  return resolveTheme(getThemePreference());
}

export function getAntdTheme(isDark) {
  const colors = isDark ? cssVars.dark : cssVars.light;

  return {
    token: {
      colorPrimary: colors.colorPrimaryAccent,
      colorLink: colors.colorBrand,
      colorLinkHover: colors.colorBrand,
      colorText: colors.colorText,
      colorTextBase: colors.colorText,
      colorTextSecondary: colors.colorMuted,
      colorBgContainer: colors.colorBgSoft,
      colorBgElevated: colors.colorBgSoft,
      colorBgBase: colors.colorBodyBg,
      colorBorder: colors.borderColor,
      colorTextLightSolid: colors.colorTextOnAccent,
      colorTextPlaceholder: colors.colorMuted,
    },
    components: {
      Button: {
        primaryColor: colors.colorTextOnAccent,
        colorPrimary: colors.colorPrimaryAccent,
        colorPrimaryHover: colors.colorPrimaryAccent,
        colorPrimaryActive: colors.colorBrand,
      },
      Input: {
        colorBgContainer: colors.colorBgSoft,
        colorText: colors.colorText,
        colorBorder: colors.borderColor,
        activeBorderColor: colors.colorBrand,
        hoverBorderColor: colors.colorBrand,
      },
      Select: {
        colorBgContainer: colors.colorBgSoft,
        colorText: colors.colorText,
        colorBorder: colors.borderColor,
      },
      Checkbox: {
        colorText: colors.colorText,
      },
      Form: {
        labelColor: colors.colorText,
      },
      Tooltip: {
        colorTextLightSolid: 'rgba(255, 255, 255, 0.85)',
        colorBgSpotlight: isDark ? 'rgba(50, 50, 50, 0.95)' : 'rgba(0, 0, 0, 0.85)',
      },
    },
  };
}
